import { MultiStepFormConfig, BaseFormData } from './components/types';
import { CriteriaStep, SingleSelectionStep, AbnLookupStep, BusinessDetailsStep, TextFieldStep, ContactDetailsStep, TrustDetailsStep, MultiInputStep } from './steps';
import { createFormOptions } from './steps/baseFormOptions';
import { createFormValidations } from './steps/baseFormValidations';
import { 
  criteriaStepInitialData, 
  singleSelectionStepInitialData, 
  abnLookupStepInitialData, 
  businessDetailsStepInitialData,
  textFieldStepInitialData,
  contactDetailsStepInitialData,
  combineStepData,
  trustDetailsStepInitialData,
  multiInputStepInitialData
} from './steps/initialData';
import { getUtmParamsForDynamics } from '@/lib/utm-tracking';

// Form options and validations - centralized in config for easy management
export const formOptions = createFormOptions({
  suitabilityCriteria: [
    "Is registered in Australia with an ABN",
    "Has a minimum of $3M annual turnover",
    "Profitable in 2 of the last 3 financial years",
    "Has a positive balance sheet net worth",
    "Is up-to-date with ATO payments",
    "Can provide current financial and management accounts"
  ],
  performanceCheckOptions: [
    {
      value: "yes",
      title: "Yes",
      description: "We made a net profit in the past 12 months."
    },
    {
      value: "no",
      title: "No",
      description: "We operated at a loss or broke even."
    },
    {
      value: "not-sure",
      title: "I'm not sure",
      description: "I don’t have that information right now."
    }
  ],
  addonServicesOptions: [
    {
      value: "term-loan",
      title: "I'm interested in the Octet Term Loan",
      description: "Access up to $5M in additional working capital."
    },
    {
      value: "not-sure",
      title: "Not at this stage",
      description: "You can always enquire about this at a later stage." 
    }
  ]
});

export const formValidations = createFormValidations({
  businessDetailsStep: (data: BaseFormData) => {
    const hasBasicFields = !!(data.countryOfRegistration && data.businessType && data.industry && data.numberOfEmployees && data.annualTurnover);

    const industryValid = data.industry === "Other" 
      ? !!(data.customIndustry && typeof data.customIndustry === 'string' && data.customIndustry.trim().length > 0)
      : !!(data.industry && data.industry !== 'Select');

    const stateValid = data.countryOfRegistration === "Other" ? true : !!(data.state && data.state !== 'Select');
    
    return hasBasicFields && industryValid && stateValid;
  },
  performanceCheckValidation: (data: BaseFormData) => !!data.performanceCheck,
  trustDetailsStep: (data: BaseFormData) => {
    // Validation for trust details - only required if business type is Trust
    if (data.businessType !== 'Trust') return true;
    
    // Basic required fields
    const hasBasicFields = !!(data.trusteeType && data.trusteeName);
    
    // ABN/ACN only required if trustee is Corporate Entity
    const needsAbn = data.trusteeType === 'Corporate Entity';
    const hasAbn = needsAbn ? !!data.trustAbnAcn : true;
    
    return hasBasicFields && hasAbn;
  },
  frequentFlyerValidation: () => {
    return true;
  },
});

// Combine initial data from selected steps
const initialFormData = combineStepData(
  criteriaStepInitialData,
  abnLookupStepInitialData,
  businessDetailsStepInitialData,
  singleSelectionStepInitialData,
  textFieldStepInitialData,
  contactDetailsStepInitialData,
  trustDetailsStepInitialData,
  multiInputStepInitialData
  // Add more step data here as you add more steps
);

export const tradeFinanceEnquireFormConfig: MultiStepFormConfig<BaseFormData> = {
  formId: 'trade-finance-enquire',
  formOptions: formOptions,
  steps: [
    {
      id: 'suitability-check',
      title: 'Quick suitability check.',
      description: 'Let\’s check a few basics to make sure <strong>Trade Finance</strong> is the right fit for your business.',
      criteriaQuestion: "Does your business meet the below criteria?",
      infoBoxText: "<strong>Not quite there yet?</strong> You might still benefit from our <a href='/products/octetpay' class='underline' target='_blank'>OctetPay</a> solution. It's a simple way to pay local or overseas suppliers with your credit or debit card.",
      component: CriteriaStep,
      validation: () => true, // No validation needed for this step
    },
    {
      id: 'business-search',
      title: 'Tell us about your business.',
      description: 'Start typing your ABN, ACN, or business name. We’ll fill in the rest.',
      validation: formValidations.abnLookupStep,
      component: AbnLookupStep,
    },
    {
      id: 'business-details',
      title: 'Confirm your business details.',
      description: 'These details help us understand your structure and business needs.',
      stepConfig: {
        showTradingName: true,
        showCountryOfRegistration: true,
        showState: true,
        showBusinessType: true,
        showIndustry: true,
        showNumberOfEmployees: true,
        showNumberOfSuppliers: true,
        showNetAssets: true,
        showAnnualTurnover: true,
        turnoverAlert: {
          triggerValues: ["$0 - $999,999", "$1,000,000 - $2,999,999"],
          message: "Trade Finance is typically suited for businesses with <strong>over $3 million in annual turnover</strong>. If you’re below this threshold, <a href='/products/octetpay' class='underline' target='_blank'>OctetPay</a> may be a better fit. It’s a simple way to pay local or overseas suppliers with your credit or debit card.",
          className: "bg-gray-100 p-0 border-0"
        }
      },
      validation: formValidations.businessDetailsStep,
      component: BusinessDetailsStep,
    },
    {
      id: 'trust-details',
      title: 'Tell us more about your trust.',
      description: 'We ask this because finance contracts are held by the trustee on behalf of the trust.',
      component: TrustDetailsStep,
      stepConfig: {
        showTrustType: false,
        showTrusteeType: true,
        showAbnAcn: true,
        showTrusteeName: true,
      },
      validation: formValidations.trustDetailsStep,
      shouldShow: (data: BaseFormData) => data.businessType === 'Trust',
    },
    {
      id: 'performance-check',
      title: 'Business performance check.',
      description: 'A quick question to help us understand your current financial position. This helps us assess suitability and guide you to the next step.',
      component: SingleSelectionStep,
      validation: formValidations.performanceCheckValidation,
      stepConfig: {
        fieldName: 'performanceCheck',
        optionsKey: 'performanceCheckOptions',
        title: 'Has your business been profitable in the last 12 months?',
        description: undefined
      }
    },
    {
      id: 'business-description',
      title: 'Describe your business.',
      description: 'This helps our team of finance specialists better understand your business needs.',
      component: TextFieldStep,
      validation: formValidations.textFieldStep,
      isOptional: true,
    },
    {
      id: 'addon-services',
      title: 'Enhance your facility with add-ons.',
      description: 'Octet offers powerful add-ons that enhance your facility. Your preferences help our team tailor the discussion to your business needs.',
      component: SingleSelectionStep,
      validation: () => true,
      isOptional: true,
      stepConfig: {
        fieldName: 'addonServices',
        optionsKey: 'addonServicesOptions',
        title: 'Would you like our team to tell you more?',
        description: undefined
      }
    },
    {
      id: 'frequent-flyer',
      title: 'Want to earn Frequent Flyer points on your payments?',
      description: 'If you\'d like to earn points on eligible payments, enter your membership numbers below. Typically, these would be the same as your ABN number. You can leave these blank. We\'ll follow up during onboarding if needed.',
      component: MultiInputStep,
      // shouldShow: (data: BaseFormData) => data.countryOfRegistration === 'Australia',
      validation: formValidations.frequentFlyerValidation,
      stepConfig: {
        inputFields: [
          {
            fieldName: 'qantasBusinessRewards',
            label: 'Qantas Business Rewards number',
            placeholder: ' ',
            inputType: 'text',
            isOptional: true, 
            helperText: 'Not a member yet? Join <a href="https://www.qantas.com/au/en/business-rewards/partner/join.html?code=PFOCTET" target="_blank">Qantas Business Rewards</a>',
          },
          {
            fieldName: 'virginAustraliaBusinessFlyer',
            label: 'Virgin Australia Business Flyer number',
            placeholder: ' ',
            inputType: 'text',
            isOptional: true,
            helperText: 'Not a member yet? Join <a href="https://www.virginaustralia.com/au/en/fly-for-business/business-flyer/business-flyer-application/?promocode=OCTETMBRM" target="_blank">Virgin Australia Business Flyer</a>',
          }
        ]
      }
    },
    {
      id: 'contact-details',
      title: 'Your contact details.',
      description: '<strong>How can we reach you?</strong> We\’ll get in touch within 1 working day to guide you through the next steps.',
      component: ContactDetailsStep,
      validation: formValidations.contactDetailsStep,
    },
  ],
  initialData: initialFormData,
  thankYouPage: {
    title: 'Thanks for your enquiry',
    subtitle: '<strong>We\’ve received your details.</strong> We will be in touch shortly to discuss your trade finance needs and next steps.',
    firstName: '{{firstName}}',
    nextStepsTitle: 'Here\’s what\’s next for',
    businessName: '{{businessName}}',
    nextStepsItems: [
      {
        icon: 'schedule',
        text: 'Our Trade Finance team will contact you within <strong>1 working day</strong>',
      },
      {
        icon: 'mail',
        text: 'You\’ll receive an <strong>email or a phone call</strong> from our specialist team.'
      },
      {
        icon: 'how_to_reg',
        text: 'We\’ll walk you through the Trade Finance process and <strong>answer any questions</strong>.'
      }
    ],
    ctaButton: {
      text: 'View Homepage',
      href: '/'
    },
    additionalInfo: 'In the meantime, stay in-the-know with helpful trade finance insights and business guides.',
    socialButton: {
      text: 'Follow Octet on LinkedIn',
      href: 'https://www.linkedin.com/company/octet-finance'
    }
  },
  onSubmit: async (data: BaseFormData) => {
    try {
      // Get UTM parameters from sessionStorage
      const utmParams = typeof window !== 'undefined' ? getUtmParamsForDynamics() : {
        ot_utmsource: '',
        ot_utmmedium: '',
        ot_utmterm: '',
        ot_utmcampaign: '',
      };

      // Map form data to Dynamics field format
      const dynamicsPayload = {
        ot_topic: 'Trade Finance application',
        pb_title: (data.title as string) || '',
        ot_firstname: (data.firstName as string) || '',
        ot_lastname: (data.lastName as string) || '',
        ot_phone: (data.phone as string) || '',
        ot_workemail: (data.workEmail as string) || '',
        // Business details
        ot_country: (data.countryOfRegistration as string) || '',
        pb_postcode: (data.postcode as string) || '',
        ot_state: (data.state as string) || '',
        ot_businesstype: (data.businessType as string) || '',
        ot_isprofitable: data.performanceCheck === 'yes' ? 'True' : data.performanceCheck === 'no' ? 'False' : 'Not Sure',
        ot_industry: (data.industry === 'Other' ? (data.customIndustry as string) || '' : (data.industry as string) || ''),
        pb_numberofemployees: (data.numberOfEmployees as string) || '',
        pb_numberofsuppliers: (data.numberOfSuppliers as string) || '',
        ot_netassets: (data.netAssets as string) || '',
        ot_annualrevenue: (data.annualTurnover as string) || '',
        ot_businessname: (data.businessName as string) || '',
        ot_abn: (data.selectedABN as string) || '',
        pb_tradingname: (data.tradingName as string) || '',
        pb_businessdescription: (data.businessDescription as string) || '',
        ot_yourenquiry: (data.message as string) || '',
        // Addon services (single value for Trade Finance)
        pb_addonservices: Array.isArray(data.addonServices) 
          ? (data.addonServices as string[]).join(', ') 
          : (data.addonServices as string) || '',
        // Trust details (if applicable)
        pb_trusttype: (data.trusteeType as string) || '',
        pb_trusteename: (data.trusteeName as string) || '',
        pb_trustabnacn: (data.trustAbnAcn as string) || '',
        // Frequent Flyer details
        pb_qantasbusinessrewards: (data.qantasBusinessRewards as string) || '',
        pb_virginaustraliabusinessflyer: (data.virginAustraliaBusinessFlyer as string) || '',
        // Marketing consent
        ot_howdidyouhearaboutus: (data.howDidYouHear as string) || '',
        pb_referredby: (data.referredBy as string) || '',
        ot_subscribedtonewsletter: (data.marketingConsent ? 'true' : 'false'),
        ot_acceptedtermsconditions: (data.termsAccepted ? 'true' : 'false'),
        // UTM parameters
        ...utmParams,
        // Include reCAPTCHA token (already added by MultiStepForm)
        recaptchaToken: (data.recaptchaToken as string) || '',
      };
      
      // Submit to Dynamics via API route
      const response = await fetch('/api/dynamics', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(dynamicsPayload),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || result.error || 'Failed to submit form');
      }

      console.info('Form submitted successfully:', result);
    } catch (error) {
      console.error('Form submission error:', error);
      throw error; // Re-throw to let MultiStepForm handle the error display
    }
  },
  showProgress: true,
  allowBackNavigation: true,
  requireRecaptcha: true,
  onStepChange: (currentStep, totalSteps) => {
    console.info(`Trade Finance Form - Step ${currentStep} of ${totalSteps}`);
  },
};
